// LaunchRulesEditor.js (cloud-admin-portal)
//
// Authoring UI for a pipeline's CONDITIONAL launch rules, shown in the OTS
// "Configure" modal below InputsParamsTable. A rule reads:
//
//   WHEN <param> <is / is not / is one of / is not one of> <value(s)>
//   THEN  <target param/input> <becomes required / cannot be / must be one of /
//          is forced to / is hidden>  [value(s)]
//
// It emits the canonical rule shape the backend + launch page consume:
//   { when:{param, op, value}, then:[{target, kind, effect, values?/value?}] }
// op ∈ equals|not_equals|in|not_in ; effect ∈ required|hidden|forbid_values|
// allow_values|set_value. `value` is a scalar for equals/not_equals/set_value
// and an array for in/not_in/forbid_values/allow_values.
//
// Dropdowns are populated from the rows currently being edited (so a rule can
// only reference fields that exist), and a controlling/target param's `choices`
// become selectable values.
import React from "react";

const OP_LABELS = { equals: "is", not_equals: "is not", in: "is one of", not_in: "is not one of" };
const ARRAY_OPS = ["in", "not_in"];
const EFFECT_LABELS = {
    required: "becomes required",
    forbid_values: "cannot be",
    allow_values: "must be one of",
    set_value: "is forced to",
    hidden: "is hidden",
};
const PARAM_ONLY = ["forbid_values", "allow_values", "set_value"];
const ARRAY_EFFECTS = ["forbid_values", "allow_values"];

const s = {
    wrap: { marginTop: 18 },
    rule: { border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, marginBottom: 10, background: "#f8fafc" },
    row: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 },
    lbl: { fontSize: 11, fontWeight: 700, color: "#475569", width: 44, flex: "0 0 auto" },
    sel: { padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, background: "#fff" },
    inp: { padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, minWidth: 150 },
    x: { border: "1px solid #fca5a5", color: "#b91c1c", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 12, padding: "3px 8px" },
    add: { border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13, padding: "5px 10px" },
    effRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6, paddingLeft: 52 },
    muted: { color: "#94a3b8", fontSize: 12 },
};

const splitCsv = (v) => String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
const choicesOf = (row) => (row ? splitCsv(row.choices) : []);

export default function LaunchRulesEditor({ rows, rules, setRules }) {
    const list = Array.isArray(rules) ? rules : [];
    const paramRows = (rows || []).filter((r) => r.kind === "param" && r.name);
    const inputRows = (rows || []).filter((r) => r.kind === "input" && r.name);
    const paramByName = Object.fromEntries(paramRows.map((r) => [r.name, r]));

    const setRule = (i, next) => setRules(list.map((r, idx) => (idx === i ? next : r)));
    const addRule = () => setRules([...list, {
        when: { param: paramRows[0] ? paramRows[0].name : "", op: "equals", value: "" },
        then: [{ target: "", kind: "param", effect: "required" }],
    }]);
    const removeRule = (i) => setRules(list.filter((_, idx) => idx !== i));

    const setWhen = (i, patch) => {
        const r = list[i];
        let when = { ...r.when, ...patch };
        // Keep value's shape consistent with the operator (scalar vs array).
        if (patch.op) {
            const wasArr = ARRAY_OPS.includes(r.when.op);
            const isArr = ARRAY_OPS.includes(patch.op);
            if (isArr && !wasArr) when.value = when.value ? [when.value] : [];
            else if (!isArr && wasArr) when.value = Array.isArray(when.value) ? (when.value[0] || "") : (when.value || "");
        }
        setRule(i, { ...r, when });
    };

    const setEffect = (i, j, patch) => {
        const r = list[i];
        const then = r.then.map((e, idx) => {
            if (idx !== j) return e;
            let ne = { ...e, ...patch };
            // Switching target kind to input forbids param-only effects.
            if (ne.kind === "input" && PARAM_ONLY.includes(ne.effect)) ne.effect = "required";
            // Normalise value container to the effect.
            if (patch.effect) {
                if (ARRAY_EFFECTS.includes(ne.effect)) { ne.values = ne.values || splitCsv(ne.value); delete ne.value; }
                else if (ne.effect === "set_value") { ne.value = ne.value != null ? ne.value : (ne.values ? (ne.values[0] || "") : ""); delete ne.values; }
                else { delete ne.values; delete ne.value; }
            }
            return ne;
        });
        setRule(i, { ...r, then });
    };
    const addEffect = (i) => { const r = list[i]; setRule(i, { ...r, then: [...r.then, { target: "", kind: "param", effect: "required" }] }); };
    const removeEffect = (i, j) => { const r = list[i]; setRule(i, { ...r, then: r.then.filter((_, idx) => idx !== j) }); };

    const renderWhenValue = (rule, i) => {
        const ctl = paramByName[rule.when.param];
        const choices = choicesOf(ctl);
        if (ARRAY_OPS.includes(rule.when.op)) {
            const cur = Array.isArray(rule.when.value) ? rule.when.value : splitCsv(rule.when.value);
            return (
                <input style={s.inp} value={cur.join(", ")} placeholder={choices.length ? `e.g. ${choices.slice(0, 3).join(", ")}` : "comma,separated"}
                    onChange={(e) => setWhen(i, { value: splitCsv(e.target.value) })} />
            );
        }
        if (choices.length) {
            return (
                <select style={s.sel} value={rule.when.value || ""} onChange={(e) => setWhen(i, { value: e.target.value })}>
                    <option value="">-- value --</option>
                    {choices.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            );
        }
        return <input style={s.inp} value={rule.when.value || ""} placeholder="value" onChange={(e) => setWhen(i, { value: e.target.value })} />;
    };

    const renderEffectValue = (eff, i, j) => {
        if (ARRAY_EFFECTS.includes(eff.effect)) {
            const tgt = paramByName[eff.target];
            const choices = choicesOf(tgt);
            const cur = Array.isArray(eff.values) ? eff.values : splitCsv(eff.values);
            return (
                <input style={s.inp} value={cur.join(", ")} placeholder={choices.length ? `e.g. ${choices.slice(0, 3).join(", ")}` : "comma,separated"}
                    onChange={(e) => setEffect(i, j, { values: splitCsv(e.target.value) })} />
            );
        }
        if (eff.effect === "set_value") {
            const tgt = paramByName[eff.target];
            const choices = choicesOf(tgt);
            if (choices.length) {
                return (
                    <select style={s.sel} value={eff.value || ""} onChange={(e) => setEffect(i, j, { value: e.target.value })}>
                        <option value="">-- value --</option>
                        {choices.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                );
            }
            return <input style={s.inp} value={eff.value || ""} placeholder="forced value" onChange={(e) => setEffect(i, j, { value: e.target.value })} />;
        }
        return null;
    };

    return (
        <div style={s.wrap}>
            <label style={{ display: "block", fontSize: 12, color: "#475569", margin: "10px 0 2px", fontWeight: 600 }}>Conditional launch rules (optional)</label>
            <p style={{ ...s.muted, marginTop: 0 }}>
                Make a field required / restrict or force its value / hide it at launch, depending on another parameter's value.
                Rules are enforced in the launch page and re-checked on the server.
            </p>

            {list.length === 0 && <p style={s.muted}>No rules yet. Add one to require or constrain fields conditionally.</p>}

            {list.map((rule, i) => (
                <div key={i} style={s.rule}>
                    <div style={s.row}>
                        <span style={s.lbl}>WHEN</span>
                        <select style={s.sel} value={rule.when.param} onChange={(e) => setWhen(i, { param: e.target.value })}>
                            <option value="">-- parameter --</option>
                            {paramRows.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
                        </select>
                        <select style={s.sel} value={rule.when.op} onChange={(e) => setWhen(i, { op: e.target.value })}>
                            {Object.keys(OP_LABELS).map((op) => <option key={op} value={op}>{OP_LABELS[op]}</option>)}
                        </select>
                        {renderWhenValue(rule, i)}
                        <button style={{ ...s.x, marginLeft: "auto" }} onClick={() => removeRule(i)}>Remove rule</button>
                    </div>

                    {rule.then.map((eff, j) => (
                        <div key={j} style={s.effRow}>
                            <span style={{ ...s.lbl, width: "auto" }}>{j === 0 ? "THEN" : "and"}</span>
                            <select style={s.sel} value={`${eff.kind}:${eff.target}`}
                                onChange={(e) => { const [kind, ...rest] = e.target.value.split(":"); setEffect(i, j, { kind, target: rest.join(":") }); }}>
                                <option value="param:">-- target field --</option>
                                {paramRows.length > 0 && (
                                    <optgroup label="Parameters">
                                        {paramRows.map((r) => <option key={`p-${r.name}`} value={`param:${r.name}`}>{r.name}</option>)}
                                    </optgroup>
                                )}
                                {inputRows.length > 0 && (
                                    <optgroup label="Input files">
                                        {inputRows.map((r) => <option key={`i-${r.name}`} value={`input:${r.name}`}>{r.name}</option>)}
                                    </optgroup>
                                )}
                            </select>
                            <select style={s.sel} value={eff.effect} onChange={(e) => setEffect(i, j, { effect: e.target.value })}>
                                {Object.keys(EFFECT_LABELS)
                                    .filter((ef) => eff.kind !== "input" || !PARAM_ONLY.includes(ef))
                                    .map((ef) => <option key={ef} value={ef}>{EFFECT_LABELS[ef]}</option>)}
                            </select>
                            {renderEffectValue(eff, i, j)}
                            {rule.then.length > 1 && <button style={s.x} onClick={() => removeEffect(i, j)}>✕</button>}
                        </div>
                    ))}
                    <div style={{ paddingLeft: 52, marginTop: 4 }}>
                        <button style={s.add} onClick={() => addEffect(i)}>+ Add effect</button>
                    </div>
                </div>
            ))}

            <button style={{ ...s.add, marginTop: 4 }} onClick={addRule} disabled={paramRows.length === 0}
                title={paramRows.length === 0 ? "Add a parameter first" : ""}>+ Add rule</button>
        </div>
    );
}
